import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Send, Square, ArrowDown, Plus, X, FileText, Code2, Copy, Check, ThumbsUp, ThumbsDown } from "lucide-react";
import { formatTimestamp, formatBotMessage } from "./utils/formatMessage";

const isSecure = window.location.protocol === 'https:';
const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
// Localhost: direct port. Production: nginx proxy (handles TLS).
const CODE_AGENT_WS_URL = process.env.REACT_APP_CODE_AGENT_WS_URL || (
  isLocalhost
    ? `ws://${window.location.hostname}:8787/ws`
    : `${isSecure ? 'wss' : 'ws'}://${window.location.host}/code-agent-ws/ws`
);
const CODE_AGENT_HTTP_URL = process.env.REACT_APP_CODE_AGENT_URL || (
  isLocalhost
    ? `http://${window.location.hostname}:8787`
    : `${isSecure ? 'https' : 'http'}://${window.location.host}/code-agent-ws`
);

const COMPOSER_MIN_HEIGHT = "20px";
const COMPOSER_MAX_HEIGHT = 200;
const SCROLL_BOTTOM_SLACK = 80;


const CodeAgentPanel = ({ username }) => {
  const { sessionId } = useParams();
  const wsRef = useRef(null);
  const bodyRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  const [connected, setConnected] = useState(false);
  const [sessionActive, setSessionActive] = useState(false);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [feedback, setFeedback] = useState({}); // { [msgId]: 'like' | 'dislike' }

  const turnRef = useRef(null);
  const followRef = useRef(true);
  const [tick, setTick] = useState(0);
  const [autoSendPending, setAutoSendPending] = useState(false);

  // Load context from sessionStorage on mount
  useEffect(() => {
    if (!sessionId || sessionId === "new") return;
    const contextKey = `code-agent-context-${sessionId}`;
    try {
      const context = sessionStorage.getItem(contextKey);
      if (context) {
        const blob = new Blob([context], { type: "text/markdown" });
        const file = new File([blob], `session-${sessionId.slice(0, 8)}.md`, { type: "text/markdown" });
        setAttachments([{ id: Date.now(), file, name: file.name, size: file.size, content: context }]);
        sessionStorage.removeItem(contextKey);
        setAutoSendPending(true);
      }
    } catch (e) {
      console.error("Failed to load context:", e);
    }
  }, [sessionId]);

  const handleMessage = useCallback((msg) => {
    switch (msg.t) {
      case "session":
        setSessionActive(msg.state === "started");
        if (msg.state === "stopped") {
          turnRef.current = null;
          setIsStreaming(false);
        }
        break;
      case "turn_start":
        turnRef.current = { id: Date.now(), text: "", startTime: Date.now() };
        setIsStreaming(true);
        setTick(n => n + 1);
        break;
      case "analyzing":
        // Just triggers the "Analyzing code..." status
        setTick(n => n + 1);
        break;
      case "response":
        // Complete response received
        if (turnRef.current) {
          turnRef.current.text = msg.text || "";
          setTick(n => n + 1);
        }
        break;
      case "turn_end":
        if (turnRef.current && turnRef.current.text) {
          const elapsed = Math.round((Date.now() - turnRef.current.startTime) / 1000);
          const finishedTurn = {
            id: turnRef.current.id,
            sender: "agent",
            text: turnRef.current.text,
            error: msg.error,
            elapsed,
            timestamp: new Date().toISOString(),
          };
          setMessages(prev => [...prev, finishedTurn]);
        }
        turnRef.current = null;
        setIsStreaming(false);
        break;
      default:
        break;
    }
  }, []);

  // WebSocket connection
  useEffect(() => {
    let reconnectTimer = null;
    const connect = () => {
      const ws = new WebSocket(CODE_AGENT_WS_URL);
      wsRef.current = ws;
      ws.onopen = () => setConnected(true);
      ws.onclose = () => {
        setConnected(false);
        setSessionActive(false);
        reconnectTimer = setTimeout(connect, 2000);
      };
      ws.onerror = () => ws.close();
      ws.onmessage = (ev) => {
        try {
          handleMessage(JSON.parse(ev.data));
        } catch (e) {
          console.error("WS message parse error:", e);
        }
      };
    };
    connect();
    return () => {
      clearTimeout(reconnectTimer);
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [handleMessage]);

  // Autoscroll
  useEffect(() => {
    if (bodyRef.current && followRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages, tick, isStreaming]);

  const handleScroll = () => {
    if (!bodyRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = bodyRef.current;
    const nearBottom = scrollHeight - scrollTop - clientHeight < SCROLL_BOTTOM_SLACK;
    followRef.current = nearBottom;
    setShowScrollDown(!nearBottom);
  };

  const scrollToBottom = () => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
      followRef.current = true;
      setShowScrollDown(false);
    }
  };

  const handleSend = useCallback(async (overrideText) => {
    const fromAuto = typeof overrideText === "string";
    const text = (fromAuto ? overrideText : draft).trim();
    if (!text && attachments.length === 0) return;
    if (isStreaming) return;

    const userMsg = {
      id: Date.now(),
      sender: "user",
      text,
      attachments: attachments.map(a => ({ name: a.name, size: a.size })),
      timestamp: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMsg]);

    if (attachments.length > 0) {
      for (const a of attachments) {
        const form = new FormData();
        form.append("file", a.file);
        form.append("text", text);
        try {
          await fetch(`${CODE_AGENT_HTTP_URL}/upload`, { method: "POST", body: form });
        } catch (e) {
          console.error("Upload failed:", e);
        }
      }
      setAttachments([]);
    } else if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ text }));
    }

    if (!fromAuto) {
      setDraft("");
      if (textareaRef.current) textareaRef.current.style.height = "auto";
    }
    followRef.current = true;
  }, [draft, attachments, isStreaming]);

  // Auto-send session context handed over from the Copilot dashboard.
  useEffect(() => {
    if (!autoSendPending || !connected || isStreaming) return;
    if (attachments.length === 0) return;
    setAutoSendPending(false);
    handleSend("Investigate the attached session context. Trace the most likely root cause in the codebase and cite where it lives.");
  }, [autoSendPending, connected, isStreaming, attachments, handleSend]);

  const stopSession = async () => {
    try {
      await fetch(`${CODE_AGENT_HTTP_URL}/stop`, { method: "POST" });
    } catch (e) {
      console.error("Stop failed:", e);
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    const newAttachments = await Promise.all(files.map(async f => {
      const attachment = {
        id: Date.now() + Math.random(),
        file: f,
        name: f.name,
        size: f.size,
      };
      // Read content for text/markdown files for preview
      if (f.type.startsWith('text/') || f.name.endsWith('.md') || f.name.endsWith('.txt')) {
        try {
          attachment.content = await f.text();
        } catch {}
      }
      return attachment;
    }));
    setAttachments(prev => [...prev, ...newAttachments]);
    e.target.value = "";
  };

  const removeAttachment = (id) => setAttachments(prev => prev.filter(a => a.id !== id));

  const copyToClipboard = async (text, msgId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(msgId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {}
  };

  const toggleFeedback = (msgId, type) => {
    setFeedback(prev => ({
      ...prev,
      [msgId]: prev[msgId] === type ? null : type
    }));
  };

  const autoResize = (el) => {
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, COMPOSER_MAX_HEIGHT) + "px";
  };

  const isEmpty = messages.length === 0 && !turnRef.current;
  const composerDisabled = isStreaming || !connected;

  return (
    <div className="d5-copilot-panel">
      {/* Header */}
      <div className="d5-copilot-header">
        <div className="d5-copilot-header-titles">
          <div className="d5-panel-icon d5-panel-icon-copilot">
            <Code2 size={15} />
          </div>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <h2 className="d5-copilot-title">Code Triage</h2>
            </div>
            <p className="d5-copilot-subtitle">
              {sessionId && sessionId !== "new" ? `From session: ${sessionId.slice(0, 8)}...` : "Investigate symptoms in the codebase"}
            </p>
          </div>
        </div>
        <div className="d5-copilot-header-meta">
          <span
            className="d5-status-dot"
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: connected ? (sessionActive ? '#10b981' : '#f59e0b') : '#ef4444',
              display: 'inline-block'
            }}
            title={connected ? (sessionActive ? "Active" : "Connected") : "Disconnected"}
          />
          {sessionActive && (
            <button type="button" className="d5-export-btn" onClick={stopSession} title="Stop session">
              <Square size={10} fill="currentColor" />
              Stop
            </button>
          )}
        </div>
      </div>

      <div className="d5-copilot-content">
        {/* Body */}
        <div className="d5-copilot-body" ref={bodyRef} onScroll={handleScroll}>
          {isEmpty && (
            <div className="d5-copilot-welcome">
              <div className="d5-copilot-welcome-icon"><Code2 size={28} /></div>
              <h3 className="d5-copilot-welcome-title">Code Triage</h3>
              <p className="d5-copilot-welcome-hint">Describe a symptom or attach session context. The agent will investigate the codebase and show where the issue might be.</p>
            </div>
          )}

          {messages.map(msg => (
            <div key={msg.id}>
              {msg.sender === "user" ? (
                <div className="d5-msg d5-msg-user">
                  {msg.attachments?.length > 0 && (
                    <div className="d5-msg-attachments">
                      {msg.attachments.map((a, i) => (
                        <span key={i} className="d5-msg-attachment-chip" title={a.name}>
                          <FileText size={12} />
                          {a.name}
                        </span>
                      ))}
                    </div>
                  )}
                  {msg.text && <div className="d5-msg-bubble d5-msg-bubble-user">{msg.text}</div>}
                  <div className="d5-msg-meta">
                    <span className="d5-msg-timestamp d5-msg-timestamp-visible">You {formatTimestamp(msg.timestamp)}</span>
                  </div>
                </div>
              ) : (
                <div className="d5-msg d5-msg-agent">
                  {msg.text && (
                    <div className="d5-msg-bubble d5-msg-bubble-agent" dangerouslySetInnerHTML={{ __html: formatBotMessage(msg.text) }} />
                  )}
                  {msg.error && <div className="d5-msg-error">Error: {msg.error}</div>}
                  <div className="d5-msg-meta">
                    <span className="d5-msg-timestamp d5-msg-timestamp-visible">Code Triage {formatTimestamp(msg.timestamp)}</span>
                    <button
                      type="button"
                      className={`d5-msg-action-btn ${copiedId === msg.id ? 'd5-msg-action-active' : ''}`}
                      onClick={() => copyToClipboard(msg.text, msg.id)}
                      title="Copy response"
                    >
                      {copiedId === msg.id ? <Check size={14} /> : <Copy size={14} />}
                    </button>
                    <button
                      type="button"
                      className={`d5-msg-action-btn ${feedback[msg.id] === 'like' ? 'd5-msg-action-liked' : ''}`}
                      onClick={() => toggleFeedback(msg.id, 'like')}
                      title="Helpful"
                    >
                      <ThumbsUp size={14} />
                    </button>
                    <button
                      type="button"
                      className={`d5-msg-action-btn ${feedback[msg.id] === 'dislike' ? 'd5-msg-action-disliked' : ''}`}
                      onClick={() => toggleFeedback(msg.id, 'dislike')}
                      title="Not helpful"
                    >
                      <ThumbsDown size={14} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Analyzing status */}
          {isStreaming && (
            <div className="d5-status-row">
              <div className="d5-typing-dots"><span /><span /><span /></div>
              <span className="d5-status-text">Analyzing code...</span>
            </div>
          )}
        </div>

        {/* Composer */}
        <div className="d5-composer">
          {showScrollDown && (
            <button type="button" className="d5-scroll-down" title="Jump to latest" onClick={scrollToBottom}>
              <ArrowDown size={15} />
            </button>
          )}
          <div className="d5-composer-box">
            {attachments.length > 0 && (
              <div className="d5-composer-attachments">
                {attachments.map(a => (
                  <div key={a.id} className="d5-attachment-chip">
                    <span className="d5-attachment-icon"><FileText size={14} /></span>
                    <button
                      type="button"
                      className="d5-attachment-info"
                      style={{ cursor: a.content ? 'pointer' : 'default', background: 'none', border: 'none', padding: 0, textAlign: 'left' }}
                      onClick={() => a.content && setPreviewAttachment(a)}
                      title={a.content ? "Click to preview" : a.name}
                    >
                      <span className="d5-attachment-name">{a.name}</span>
                      <span className="d5-attachment-size">{(a.size / 1024).toFixed(1)} KB</span>
                    </button>
                    <button type="button" className="d5-attachment-remove" title="Remove" onClick={() => removeAttachment(a.id)}>
                      <X size={10} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="d5-composer-row">
              <input ref={fileInputRef} type="file" multiple style={{ display: "none" }} onChange={handleFileSelect} />
              <button
                type="button"
                className="d5-composer-attach"
                title="Attach files"
                disabled={composerDisabled}
                onClick={() => fileInputRef.current?.click()}
              >
                <Plus size={16} />
              </button>
              <textarea
                ref={textareaRef}
                className="d5-composer-input"
                placeholder={!connected ? "Connecting…" : isStreaming ? "Analyzing codebase…" : isEmpty ? "Describe a symptom or paste a log…" : "Ask follow-up…"}
                value={draft}
                disabled={composerDisabled}
                rows={1}
                style={{ minHeight: COMPOSER_MIN_HEIGHT, maxHeight: `${COMPOSER_MAX_HEIGHT}px` }}
                onChange={(e) => { setDraft(e.target.value); autoResize(e.target); }}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              />
              {isStreaming ? (
                <button type="button" className="d5-composer-stop" title="Stop" onClick={stopSession}>
                  <Square size={10} fill="currentColor" strokeWidth={0} />
                </button>
              ) : (
                <button type="button" className="d5-composer-send" disabled={composerDisabled} onClick={handleSend} title="Send">
                  <Send size={16} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Attachment Preview Modal */}
      {previewAttachment && (
        <div className="d5-preview-overlay" onClick={() => setPreviewAttachment(null)}>
          <div className="d5-preview-modal" onClick={e => e.stopPropagation()}>
            <div className="d5-preview-header">
              <span className="d5-preview-title">
                <FileText size={16} />
                {previewAttachment.name}
              </span>
              <button type="button" className="d5-preview-close" onClick={() => setPreviewAttachment(null)}>
                <X size={18} />
              </button>
            </div>
            <div className="d5-preview-content">
              <div dangerouslySetInnerHTML={{ __html: formatBotMessage(previewAttachment.content || '') }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CodeAgentPanel;
